// src/admin/pages/employee/EmployeeTable.tsx
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Employee } from '../../../enums/employee';
import { Warehouse } from '../../../enums/product';

interface EmployeeTableProps {
  employees: Employee[];
  branches: Warehouse[];
  onEdit: (employee: Employee) => void;
  onDelete: (employee: Employee) => void;
  deleteLoading: string;
  isDark?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

// ── Theme-aware chip style helper — mirrors BranchTable's getSoftChipSx ──────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-label': { color: p.text },
  };
};

const STATUS_CHIP_COLOR: Record<string, string> = {
  Active: 'success',
  'On Leave': 'warning',
  Suspended: 'error',
  Terminated: 'default',
};

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  employees,
  branches,
  onEdit,
  onDelete,
  deleteLoading,
  isDark = false,
  canEdit = false,
  canDelete = false,
}) => {
  return (
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 1000 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Branch</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Basic Salary</TableCell>
            <TableCell>Commission %</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {employees.map((emp) => (
            <TableRow
              key={emp.id}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                '&:last-child td, &:last-child th': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                    {emp.fullName?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {emp.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {emp.email || 'No email'}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {emp.jobRole}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {branches.find((b) => b.id === emp.branchId)?.name || '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{emp.phone}</Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{emp.basicSalary.toLocaleString()}</Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {emp.commissionRate || 0}%
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  label={emp.status}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(STATUS_CHIP_COLOR[emp.status] || 'default', isDark)}
                />
              </TableCell>

              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                  {canEdit && (
                    <Tooltip title="Edit Employee">
                      <IconButton onClick={() => onEdit(emp)} size="small" color="warning">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Remove Employee">
                      <IconButton
                        onClick={() => onDelete(emp)}
                        disabled={deleteLoading === emp.id}
                        size="small"
                        color="error"
                      >
                        {deleteLoading === emp.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EmployeeTable;
